import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ArticleIcon from "@mui/icons-material/Article";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GifBoxIcon from "@mui/icons-material/GifBox";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MicIcon from "@mui/icons-material/Mic";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import GppGoodIcon from "@mui/icons-material/GppGood";

import { StreamSummaryBanner } from "../../../components/immersive-panel/StreamSummaryBanner";
import { FeatureDefinition } from "../ImmersivePanelSetupPage";

type PreviewPanelProps = {
  features: FeatureDefinition[];
  activeIds: string[];
};

type PanelConfig = {
  showChat: boolean;
  showRead: boolean;
  showAI: boolean;
  showShop: boolean;
  showQuiz: boolean;
  showAnnouncement: boolean;
  showAd: boolean;
  showAiEngagement: boolean;
  showModeration: boolean;
  showVoice: boolean; 
};

export const PANEL_WIDTH = 460;
export const PANEL_HEIGHT = 900;

const HeaderTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: 15,
  borderRadius: 10,
  color: alpha("#FFFFFF", 0.75),
  minHeight: 46,
  minWidth: 0,
  padding: "6px 22px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontFamily: "'Inter', sans-serif",

  /* 👇 give the first tab breathing room from the rounded corner */
  "&:first-of-type": {
    marginLeft: theme.spacing(3.5),
  },

  "&.Mui-selected": {
    background:
      "linear-gradient(135deg, rgba(107,79,255,0.65) 0%, rgba(139,92,246,0.85) 100%)",
    color: "#FFFFFF",
    boxShadow: "0 8px 20px rgba(107,79,255,0.35)",
  },
  "&:hover": {
    color: "#FFFFFF",
    background: alpha("#6B4FFF", 0.25),
  },
}));

const SCROLLBAR_SX = {
  scrollbarWidth: "thin",                   // Firefox
  scrollbarColor: "#7C3AED transparent",    // Firefox (thumb track)

  "&::-webkit-scrollbar": {
    width: 10,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(180deg, #7C3AED 0%, #6366F1 100%)",
    borderRadius: 999,
    border: "2px solid rgba(5,7,22,0.5)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "linear-gradient(180deg, #8B5CF6 0%, #818CF8 100%)",
  },
};

// MessageCard
const MessageCard: React.FC<{ author: string; text: string; avatar: string; ai?: boolean }> =
({ author, text, avatar, ai }) => (
  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
    <AvatarBubble label={avatar} />
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        border: `1px solid ${ai ? alpha("#8B5CF6", 0.45) : alpha("#FFFFFF", 0.10)}`,
        background: ai
          ? "linear-gradient(180deg, rgba(44, 28, 80, 0.75) 0%, rgba(23, 19, 50, 0.85) 100%)"
          : "linear-gradient(180deg, rgba(20, 24, 52, 0.85) 0%, rgba(14, 18, 40, 0.85) 100%)",
        backdropFilter: "blur(6px)",
        px: 1.5,
        py: 1.25,
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          borderColor: ai ? alpha("#8B5CF6", 0.65) : alpha("#FFFFFF", 0.18),
          boxShadow: "0 6px 18px rgba(5,7,22,0.35)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.76), fontWeight: 700 }}>
          {author}
        </Typography>
        {ai && (
          <AutoAwesomeIcon
            fontSize="inherit"
            sx={{ fontSize: 14, color: alpha("#C4B5FD", 0.9) }}
            titleAccess="Suggested"
          />
        )}
        <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: alpha("#FFFFFF", 0.28) }} />
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.45) }}>
          now
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mt: 0.5, lineHeight: 1.6 }}>
        {text}
      </Typography>
    </Box>
  </Box>
);

const ModeratedMessageCard: React.FC<{ author: string; reason?: string }> = ({ author, reason }) => (
  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
    <AvatarBubble label={author.slice(0,2).toUpperCase()} />
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        border: `1px dashed ${alpha("#FFFFFF", 0.18)}`,
        background: alpha("#0F1420", 0.55),
        backdropFilter: "blur(4px)",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <GppGoodIcon sx={{ fontSize: 16, color: alpha("#A78BFA", 0.95) }} />
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.65), fontWeight: 700 }}>
          Message removed
        </Typography>
        <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: alpha("#FFFFFF", 0.28) }} />
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.45) }}>
          by AI moderation
        </Typography>
      </Stack>
      {reason && (
        <Typography variant="caption" sx={{ mt: 0.75, color: alpha("#FFFFFF", 0.5), lineHeight: 1.5 }}>
          {reason}
        </Typography>
      )}
    </Box>
  </Box>
);


const deriveConfig = (ids: string[]): PanelConfig => {
  const set = new Set(ids);
  return {
    // ✅ Live chat is considered ON if user explicitly turned on chat_room
    // or if either ai_chat_engagement or ai_moderation is active
    showChat: ["chat_room", "ai_chat_engagement", "ai_moderation"].some((id) => set.has(id)),

    showRead: false,
    showAI: ["ai_qa", "logistics_ai", "voice_ai", "smart_ads", "stream_understanding"].some((id) => set.has(id)),
    showShop: set.has("affiliate_store"),
    showQuiz: set.has("live_quizzes"),
    showAnnouncement: set.has("attendee_blasts") || set.has("highlights") || set.has("stream_understanding"),
    showAd: set.has("smart_ads"),
    showAiEngagement: set.has("ai_chat_engagement"),
    showModeration: set.has("ai_moderation"),
    showVoice: set.has("voice_ai"),
  };
};

const PreviewPanel: React.FC<PreviewPanelProps> = ({ features, activeIds }) => {
  const config = useMemo(() => deriveConfig(activeIds), [activeIds]);
  const [activeTab, setActiveTab] = useState(0);

  const activationChips = useMemo(
    () =>
      activeIds
        .map((id) => features.find((feature) => feature.id === id))
        .filter((feature): feature is FeatureDefinition => Boolean(feature)),
    [activeIds, features],
  );

const tabs = useMemo(() => {
  type TabItem = { id: string; label: string; icon: React.ReactElement; content: React.ReactNode };

  const items: TabItem[] = [
    {
      id: "chat",
      // Optional: rename label when chat is off so it makes sense to users
      label: config.showChat ? "Chat" : "Main",
      icon: <ChatBubbleIcon fontSize="small" />,
      content: <ChatPreview config={config} />, // ← banner + quiz render here regardless
    },
  ];

  if (config.showAI) {
    items.push({
      id: "ai",
      label: "AI",
      icon: <AutoAwesomeIcon fontSize="small" />,
      content: <AiPreview hasVoice={config.showVoice} />,
    });
  }

  if (config.showShop) {
    items.push({
      id: "shop",
      label: "Shop",
      icon: <LocalMallIcon fontSize="small" />,
      content: <ShopPreview />,
    });
  }

  return items;
}, [config]);



  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        display: "flex",
        flexDirection: "column",
        borderRadius: 24,
        overflow: "hidden",
        border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
        background: "linear-gradient(180deg, #070b1e 0%, #050716 100%)",
        boxShadow: "0 28px 52px rgba(5,7,22,0.6)",
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          background: "linear-gradient(180deg, rgba(15,18,42,0.95) 0%, rgba(9,12,28,0.9) 100%)",
        }}
      >
        <Tabs
          value={Math.min(activeTab, tabs.length - 1)}
          onChange={(_, next) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            ".MuiTabs-flexContainer": { gap: 8 },
            ".MuiTabs-scrollButtons": {
              width: 32,
              height: 32,
              borderRadius: "50%",
              color: alpha("#FFFFFF", 0.6),
              "&.Mui-disabled": { opacity: 0.2 },
            },
          }}
        >
          {tabs.map((tab) => (
            <HeaderTab key={tab.id} icon={tab.icon} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Divider sx={{ borderColor: alpha("#FFFFFF", 0.08) }} />

      <Box sx={{ flex: 1, overflow: "hidden" }}>{tabs[Math.min(activeTab, tabs.length - 1)]?.content}</Box>

      <Divider sx={{ borderColor: alpha("#FFFFFF", 0.08) }} />

      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          background: alpha("#FFFFFF", 0.03),
        }}
      >
        {activationChips.length ? (
          activationChips.map((feature) => (
            <Chip
              key={feature.id}
              label={feature.label}
              size="small"
              sx={{
                bgcolor: feature.tier === "advanced" ? alpha("#8B5CF6", 0.25) : alpha("#4F46E5", 0.2),
                color: "#E0E7FF",
                fontWeight: 600,
              }}
            />
          ))
        ) : (
          <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.45) }}>
            Toggle modules above to see them in the panel preview.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ChatPreview: React.FC<{ config: PanelConfig }> = ({ config }) => {
  const chatEnabled = config.showChat; // ← single switch

  const base = [
    { author: "RK89", text: "Sanctions take forever to kick in lol", avatar: "RK" },
    { author: "ErikM", text: "@RK89 yeah, surprised more people aren't talking about the LNG stuff tbh.", avatar: "EM" },
    { author: "SaraG", text: "Any numbers on how many refugees Europe took in?", avatar: "SG" },
    { author: "LeoV", text: "I get why US has global focus, but sometimes feels like Europe handles more here??", avatar: "LV" },
    { author: "M_Santos", text: "Unicorns staying private is wild tbh.", avatar: "MS" },
    { author: "averylilia", text: "Lesotho for shirts? Didn't see that coming.", avatar: "AL" },
    { author: "Producer", text: "Everything ready for the next segment!", avatar: "PR" },
  ];

  const withAiEngagement = config.showAiEngagement
    ? [
        base[0],
        {
          author: "secretly_ai",
          text: "Quick context: the guest just connected logging policy to supply chain shifts. Want a 20s recap?",
          avatar: "MT",
          ai: true,
        },
        ...base.slice(1),
      ]
    : base;

  const messages = config.showModeration
    ? [
        ...withAiEngagement.slice(0, 2),
        { __moderated: true, author: "guest123", reason: "Possible personal info. Please keep it safe & on-topic." },
        ...withAiEngagement.slice(2),
      ]
    : withAiEngagement;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ✅ Announcement / Highlights banner should ALWAYS be able to show */}
      {config.showAnnouncement && (
        <StreamSummaryBanner
          summary="The stream just captured an emotional moment as we witnessed beautiful trees being taken down, sparking a deep reflection on spirituality and connection to nature. Stick around as the discussion gets even more thought-provoking!"
          hlsUrl="preview://immersive"
          onClose={() => {}}
          sx={{ position: "static", width: "100%", px: 0, transform: "none", boxShadow: "none", borderRadius: 3 }}
        />
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 2, pb: 1, ...SCROLLBAR_SX }}>
        {config.showAd && (
          <AdCard headline="Earn up to $480/hr" subhead="You've got great skills. Share them with Bolt Academy." />
        )}

        {/* ✅ Only hide the conversation when chat is off */}
        {chatEnabled && (
          <Stack spacing={1.25} sx={{ mt: config.showAd ? 2 : 0 }}>
            {messages.map((m: any, i: number) =>
              m.__moderated ? (
                <ModeratedMessageCard key={`mod-${i}`} author={m.author} reason={m.reason} />
              ) : (
                <MessageCard key={`${m.author}-${i}`} author={m.author} text={m.text} avatar={m.avatar} ai={m.ai === true} />
              )
            )}
          </Stack>
        )}

        {/* (optional) If chat is off, show a tiny hint so the tab doesn’t feel empty */}
        {!chatEnabled && !config.showQuiz && !config.showAnnouncement && (
          <Typography variant="body2" sx={{ mt: 2, color: alpha("#FFFFFF", 0.5) }}>
            Live chat is turned off.
          </Typography>
        )}

        {config.showAd && (
          <Box sx={{ mt: 2 }}>
            <AdCard
              headline="Earn up to $480/hr"
              subhead="Share your expertise in live sessions and grow your community."
            />
          </Box>
        )}
      </Box>

      {/* ✅ Quiz should ALWAYS be able to show */}
      {config.showQuiz && (
        <Box sx={{ px: 3 }}>
          <QuizCard />
        </Box>
      )}

      {/* ✅ Composer is hidden when chat is off */}
      <Box sx={{ px: 3, pb: 3 }}>
        {chatEnabled ? (
          <MessageComposer />
        ) : (
          <Chip
            label="Live chat is off"
            size="small"
            sx={{
              borderRadius: 2,
              color: "#C7D2FE",
              bgcolor: alpha("#6366F1", 0.18),
              border: `1px solid ${alpha("#6366F1", 0.35)}`,
              fontWeight: 700,
            }}
          />
        )}
      </Box>
    </Box>
  );
};




const AdCard: React.FC<{ headline: string; subhead: string }> = ({ headline, subhead }) => (
  <Box
    sx={{
      borderRadius: 3,
      border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
      overflow: "hidden",
      background: "linear-gradient(135deg, rgba(255,180,162,0.2) 0%, rgba(255,99,132,0.35) 100%)",
    }}
  >
    <Box
      sx={{
        height: 120,
        backgroundImage:
          "linear-gradient(135deg, rgba(255,180,162,0.6) 0%, rgba(255,99,132,0.75) 70%, rgba(255,214,102,0.6) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ color: "#0D1022", fontWeight: 700 }}>
          {headline}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha("#0D1022", 0.75), mt: 0.5 }}>
          {subhead}
        </Typography>
        <Button
          size="small"
          sx={{
            mt: 1.5,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 600,
            bgcolor: "#0D1022",
            color: "#FFFFFF",
            px: 2,
            "&:hover": { bgcolor: alpha("#0D1022", 0.85) },
          }}
        >
          Learn more
        </Button>
      </Box>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "18px",
          bgcolor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          color: "#0D1022",
        }}
      >
        QR
      </Box>
    </Box>
  </Box>
);

const AvatarBubble: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      position: "relative",
      width: 36,
      height: 36,
      borderRadius: "50%",
      flexShrink: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: -2,
        borderRadius: "50%",
        background: "linear-gradient(135deg, rgba(107,79,255,0.9), rgba(84,46,255,0.9))",
        filter: "blur(4px)",
        opacity: 0.6,
      },
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(135deg, rgba(107,79,255,0.75) 0%, rgba(84,46,255,0.9) 100%)",
      border: "1px solid rgba(255,255,255,0.22)",
      color: "#FFFFFF",
      fontWeight: 700,
      letterSpacing: 0.2,
    }}
  >
    {label}
  </Box>
);

const QuizCard = () => {
  const options = [
    { label: "Nantu and his people", key: "A" },
    { label: "Friends of the Earth", key: "B" },
    { label: "Amazon Conservation Team", key: "C" },
    { label: "The World Wildlife Fund", key: "D" },
  ];

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha("#6B4FFF", 0.38)}`,
        background: alpha("#6B4FFF", 0.10),
        p: 1.75,                  // was 2.5 → tighter
        display: "flex",
        flexDirection: "column",
        gap: 1.25,                // was 1.5
      }}
    >
      {/* header */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="overline" sx={{ color: "#A5B4FC", fontWeight: 800, letterSpacing: 1.2, lineHeight: 1 }}>
          LIVE QUIZ
        </Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#A5B4FC" }} />
      </Stack>

      {/* question (smaller) */}
      <Typography
        variant="body2" // was body1
        sx={{ color: "#FFFFFF", fontWeight: 600, lineHeight: 1.5 }}
      >
        Which group is working to protect the rainforest and their way of life by keeping roads out?
      </Typography>

      {/* 2×2 grid (denser) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 0.75,             // was 1
          mt: 0.25,
        }}
      >
          {options.map((opt) => (
            <Button
              key={opt.key}
              variant="outlined"
              size="small"
              fullWidth
              startIcon={
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1,
                    color: "#FFFFFF",
                    background:
                      "linear-gradient(135deg, rgba(124,58,237,0.9), rgba(99,102,241,0.95))",
                    border: `1px solid ${alpha("#FFFFFF", 0.18)}`,
                    boxShadow: "0 2px 8px rgba(124,58,237,0.35), inset 0 0 0 1px rgba(255,255,255,0.12)",
                    flexShrink: 0,
                  }}
                >
                  <Box component="span" sx={{ position: "relative", top: "0.5px" }}>
                    {opt.key}
                  </Box>
                </Box>
              }
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",     // top-align icon and text for multiline
                textAlign: "left",
                borderRadius: 2,
                borderColor: alpha("#FFFFFF", 0.12),
                color: "#E0E7FF",
                textTransform: "none",
                fontWeight: 600,
                px: 1.25,
                py: 1,                        // tighter vertical padding
                height: "auto",               // allow to grow with lines
                minHeight: 38,
                "& .MuiButton-startIcon": {
                  mr: 1,
                  mt: 0.25,                   // nudge badge down a touch for visual centering
                },
                "&:hover": { borderColor: alpha("#6B4FFF", 0.5), bgcolor: alpha("#6B4FFF", 0.07) },
              }}
            >
              <Typography
                variant="body2"               // smaller type
                sx={{
                  color: "#E0E7FF",
                  fontSize: 13,               // a bit smaller than default body2
                  lineHeight: 1.35,
                  whiteSpace: "normal",       // ✅ allow wrapping
                  wordBreak: "break-word",
                }}
              >
                {opt.label}
              </Typography>
            </Button>
          ))}
      </Box>
    </Box>
  );
};


const ComposerShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      borderRadius: 10,
      border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
      bgcolor: alpha("#13172F", 0.95),
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const MessageComposer = () => (
  <ComposerShell>
    <Typography variant="body2" sx={{ flex: 1, color: alpha("#FFFFFF", 0.45) }}>
      Type something...
    </Typography>
    <Button
      size="small"
      sx={{
        minWidth: 36,
        height: 32,
        borderRadius: 2,
        bgcolor: alpha("#6B4FFF", 0.15),
        color: "#C4B5FD",
        "&:hover": { bgcolor: alpha("#6B4FFF", 0.25) },
      }}
    >
      <EmojiEmotionsIcon fontSize="small" />
    </Button>
    <Button
      size="small"
      sx={{
        minWidth: 36,
        height: 32,
        borderRadius: 2,
        bgcolor: alpha("#6B4FFF", 0.25),
        color: "#C4B5FD",
        "&:hover": { bgcolor: alpha("#6B4FFF", 0.35) },
      }}
    >
      <GifBoxIcon fontSize="small" />
    </Button>
    <Button
      size="small"
      sx={{
        minWidth: 44,
        height: 34,
        borderRadius: 999,
        px: 1.5,
        bgcolor: "#6C47FF",
        color: "#FFFFFF",
        "&:hover": { bgcolor: "#5836E0" },
      }}
    >
      <SendRoundedIcon fontSize="small" />
    </Button>
  </ComposerShell>
);

const AiComposer: React.FC<{ placeholder: string }> = ({ placeholder }) => (
  <ComposerShell>
    <Typography variant="body2" sx={{ flex: 1, color: alpha("#FFFFFF", 0.45) }}>
      {placeholder}
    </Typography>
    <Button
      size="small"
      sx={{
        minWidth: 36,
        height: 32,
        borderRadius: 2,
        bgcolor: alpha("#6B4FFF", 0.15),
        color: "#C4B5FD",
        "&:hover": { bgcolor: alpha("#6B4FFF", 0.25) },
      }}
    >
      <MicIcon fontSize="small" />
    </Button>
    <Button
      size="small"
      sx={{
        minWidth: 44,
        height: 34,
        borderRadius: 999,
        px: 1.5,
        bgcolor: "#6C47FF",
        color: "#FFFFFF",
        "&:hover": { bgcolor: "#5836E0" },
      }}
    >
      <SendRoundedIcon fontSize="small" />
    </Button>
  </ComposerShell>
);

const DonateBar = () => (
  <Box
    sx={{
      borderRadius: 10,
      border: `1px solid ${alpha("#6B4FFF", 0.35)}`,
      bgcolor: "linear-gradient(135deg, rgba(107,79,255,0.2) 0%, rgba(107,79,255,0.35) 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 1.1,
      px: 2,
    }}
  >
    <Chip
      label="Donate"
      icon={<FavoriteIcon sx={{ color: "#FFFFFF" }} />}
      sx={{
        bgcolor: "linear-gradient(135deg, #FD4A85 0%, #FF6A3D 100%)",
        color: "#FFFFFF",
        fontWeight: 700,
        px: 1,
        "& .MuiChip-icon": { color: "#FFFFFF" },
      }}
    />
    <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.85) }}>
      to support your favourite streamers!
    </Typography>
  </Box>
);

const AiPreview: React.FC<{ hasVoice: boolean }> = ({ hasVoice }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    {/* scrollable content */}
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 3,
        py: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        ...SCROLLBAR_SX,
      }}
    >
      <Box
        sx={{
          mt: 1,
          borderRadius: 4,
          border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
          bgcolor: alpha("#13172F", 0.95),
          p: 3,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(107, 79, 255, 0.4), rgba(138, 85, 255, 0.8))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
            IRIS - Your Stream Companion
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.65), mt: 1 }}>
            Ask anything about the live stream, surface quick summaries, or get AI to track key moments for you.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center">
          {["What's happening now?", "Summarize the stream", "Key moments"].map((label) => (
            <Button
              key={label}
              variant="outlined"
              sx={{
                borderRadius: 999,
                borderColor: alpha("#FFFFFF", 0.15),
                color: "#E0E7FF",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                "&:hover": { borderColor: alpha("#8B5CF6", 0.5) },
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>
        {hasVoice && (  
        <Button
          startIcon={<MicIcon />}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            background: "linear-gradient(135deg, rgba(124, 58, 237, 0.8), rgba(99, 102, 241, 0.95))",
            color: "#FFFFFF",
            "&:hover": { background: "linear-gradient(135deg, rgba(124, 58, 237, 0.95), rgba(99, 102, 241, 1))" },
          }}
        >
          Talk to IRIS
        </Button>
        )}
      </Box>
    </Box>

    {/* bottom composer — same level as chat */}
    <Box sx={{ px: 3, pb: 3 }}>
      <AiComposer placeholder="Type something..." />
    </Box>
  </Box>
);

const ShopPreview = () => (
  <Box sx={{ height: "100%", overflowY: "auto", px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
    <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
      Featured drops
    </Typography>

    {[1, 2].map((index) => (
      <Box
        key={index}
        sx={{
          borderRadius: 18,
          border: `1px solid ${alpha("#FFFFFF", 0.1)}`,
          bgcolor: alpha("#141834", 0.96),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: 140,
            background: "linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(139,92,246,0.45) 100%)",
          }}
        />
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="subtitle1" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
            Creator summit hoodie
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.65), lineHeight: 1.6 }}>
            Limited-run drop with stitched sleeve detailing and an NFC tag for exclusive content.
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" sx={{ color: "#C4B5FD", fontWeight: 700 }}>
              $68.00
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#6C47FF",
                "&:hover": { bgcolor: "#5A3AE0" },
              }}
            >
              View
            </Button>
          </Stack>
        </Box>
      </Box>
    ))}
  </Box>
);

const ReadPreview = () => (
  <Box sx={{ height: "100%", overflowY: "auto", px: 3, py: 3 }}>
    <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
      Stream summary
    </Typography>
    <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.65), mt: 1.5, lineHeight: 1.7 }}>
      An AI-generated digest appears here when highlights are active. Attendees can skim top takeaways and revisit key
      quotes without scrubbing the video.
    </Typography>
    <Divider sx={{ borderColor: alpha("#FFFFFF", 0.08), my: 3 }} />
    {[1, 2, 3].map((idx) => (
      <Box key={idx} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ color: "#E0E7FF", fontWeight: 600 }}>
          Moment #{idx}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.6), mt: 0.75, lineHeight: 1.6 }}>
          The panel just unpacked how budgets shape creative direction. IRIS bookmarked the exchange for quick recap.
        </Typography>
        <Button
          size="small"
          startIcon={<PlayCircleOutlineIcon />}
          sx={{
            mt: 1,
            textTransform: "none",
            color: "#8B5CF6",
            fontWeight: 600,
          }}
        >
          Jump to moment
        </Button>
      </Box>
    ))}
  </Box>
);

const SettingsPreview = () => (
  <Box sx={{ height: "100%", overflowY: "auto", px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
    <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
      Quick settings
    </Typography>
    {[
      { label: "Enable slow mode", description: "Limit chat to one message every 5 seconds." },
      { label: "Pin sponsor banner", description: "Keep partner creatives visible for the next 15 minutes." },
      { label: "Allow GIF replies", description: "Let viewers react with curated GIFs in real time." },
    ].map((setting) => (
      <Box
        key={setting.label}
        sx={{
          borderRadius: 12,
          border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
          bgcolor: alpha("#151834", 0.92),
          p: 2.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "#FFFFFF", fontWeight: 600 }}>
          {setting.label}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.55), mt: 0.5, lineHeight: 1.6 }}>
          {setting.description}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default PreviewPanel;
